import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiX,
  FiMail,
  FiGift,
  FiFileText,
  FiMusic,
  FiShoppingBag,
  FiCheck,
  FiAlertTriangle,
} from "react-icons/fi";
import {
  ModalOverlay,
  ModalContainer,
  ModalHeader,
  CloseButton,
  ModalContent,
  EmailDisplay,
  PreferencesSection,
  PreferencesHead,
  PreferencesTitle,
  SelectAllButton,
  CheckboxGroup,
  CheckboxItem,
  CheckboxIcon,
  CheckboxContent,
  CustomCheckbox,
  ErrorMessage,
  ModalFooter,
  FooterNote,
  ModalActions,
  ModalButton,
  ButtonSpinner,
} from "./NewsletterModalStyles";
import useScrollLock from "../../hooks/useScrollLock";

const PREFERENCE_OPTIONS = [
  {
    key: "newProducts",
    title: "Novi proizvodi",
    description: "Nove majice, dodaci i kolekcije",
    icon: <FiShoppingBag size={16} />,
  },
  {
    key: "discounts",
    title: "Popusti i akcije",
    description: "Ekskluzivni popusti, rasprodaje i specijalne ponude",
    icon: <FiGift size={16} />,
  },
  {
    key: "news",
    title: "Vesti i najave",
    description: "Najnovije vesti sa scene i iz naše zajednice",
    icon: <FiFileText size={16} />,
  },
  {
    key: "newBands",
    title: "Novi bendovi",
    description: "Kada dodamo nove bendove u kolekciju",
    icon: <FiMusic size={16} />,
  },
];

const EMPTY_PREFERENCES = {
  newProducts: false,
  discounts: false,
  news: false,
  newBands: false,
};

const NewsletterPreferencesModal = ({
  isOpen,
  onClose,
  email,
  onSubscribe,
  loading = false,
  error = null,
}) => {
  const [preferences, setPreferences] = useState(EMPTY_PREFERENCES);
  const [allSelected, setAllSelected] = useState(false);

  useEffect(() => {
    const selectedCount = Object.values(preferences).filter(Boolean).length;
    setAllSelected(selectedCount === PREFERENCE_OPTIONS.length);
  }, [preferences]);

  const handlePreferenceChange = (key) => {
    setPreferences((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleSelectAll = () => {
    const newValue = !allSelected;
    const newPreferences = {};
    PREFERENCE_OPTIONS.forEach((option) => {
      newPreferences[option.key] = newValue;
    });
    setPreferences(newPreferences);
  };

  const handleSubscribe = () => {
    const hasSelected = Object.values(preferences).some(Boolean);
    if (!hasSelected) return;

    onSubscribe({ email, preferences });
    setPreferences(EMPTY_PREFERENCES);
  };

  const hasSelectedPreferences = Object.values(preferences).some(Boolean);

  useScrollLock(isOpen);

  return (
    <AnimatePresence>
      {isOpen && (
        <ModalOverlay
          as={motion.div}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <ModalContainer
            as={motion.div}
            role="dialog"
            aria-modal="true"
            initial={{ scale: 0.96, opacity: 0, y: 8 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.96, opacity: 0, y: 8 }}
            transition={{ duration: 0.18 }}
            onClick={(e) => e.stopPropagation()}
          >
            <ModalHeader>
              <h3>
                <FiMail size={17} />
                Podesi preferencije
              </h3>
              <CloseButton type="button" onClick={onClose} aria-label="Zatvori">
                <FiX size={16} />
              </CloseButton>
            </ModalHeader>

            <ModalContent>
              <EmailDisplay>
                <FiMail size={15} />
                <span>{email}</span>
              </EmailDisplay>

              {error && (
                <ErrorMessage>
                  <FiAlertTriangle size={15} />
                  {error}
                </ErrorMessage>
              )}

              <PreferencesSection>
                <PreferencesHead>
                  <PreferencesTitle>Šta te zanima?</PreferencesTitle>
                  <SelectAllButton
                    type="button"
                    onClick={handleSelectAll}
                    className={allSelected ? "selected" : ""}
                  >
                    {allSelected ? "Poništi sve" : "Označi sve"}
                  </SelectAllButton>
                </PreferencesHead>

                <CheckboxGroup>
                  {PREFERENCE_OPTIONS.map((option) => (
                    <CheckboxItem
                      key={option.key}
                      className={preferences[option.key] ? "checked" : ""}
                    >
                      <CheckboxIcon>{option.icon}</CheckboxIcon>
                      <CheckboxContent>
                        <h5>{option.title}</h5>
                        <p>{option.description}</p>
                      </CheckboxContent>
                      <CustomCheckbox
                        type="checkbox"
                        checked={preferences[option.key]}
                        onChange={() => handlePreferenceChange(option.key)}
                      />
                    </CheckboxItem>
                  ))}
                </CheckboxGroup>
              </PreferencesSection>
            </ModalContent>

            <ModalFooter>
              <FooterNote>
                Odjava jednim klikom, u svakom mejlu koji pošaljemo.
              </FooterNote>
              <ModalActions>
                <ModalButton
                  type="button"
                  className="secondary"
                  onClick={onClose}
                >
                  Otkaži
                </ModalButton>
                <ModalButton
                  type="button"
                  className="primary"
                  disabled={!hasSelectedPreferences || loading}
                  onClick={handleSubscribe}
                >
                  {loading ? (
                    <>
                      <ButtonSpinner />
                      Kreiram...
                    </>
                  ) : (
                    <>
                      <FiCheck size={16} />
                      Pretplati se
                    </>
                  )}
                </ModalButton>
              </ModalActions>
            </ModalFooter>
          </ModalContainer>
        </ModalOverlay>
      )}
    </AnimatePresence>
  );
};

export default NewsletterPreferencesModal;
